import { Vector2 } from "../../common/vector2.js";
import { Game } from "../../game.js";
import { LocalizationManager } from "../../localization/localization_manager.js";
import { isPlaceBoxItem, type TournamentStanding } from "../../tournament/tournament_service.js";
import { UIScrollableContainerCSS } from "../common_ui_elements/scrollable_container_css.js";
import { DomTooltip, type TooltipAnchorRect } from "../tooltip/dom_tooltip.js";
import { RewardTooltip } from "../tooltip/reward_tooltip.js";
import { TournamentStandingsRow, type RowBounds } from "./tournament_standings_row.js";

interface ListRow
{
  row: TournamentStandingsRow;
  slot_y: number;
}

export class TournamentStandingsList extends UIScrollableContainerCSS
{
  public static readonly ITEM_W = 906;
  public static readonly ITEM_H = 150;
  private static readonly ROW_STEP = 146;
  private static readonly ROWS_EDGE_CLIP = 5;
  private static readonly ROWS_UNDER_CARD = 30;
  /** A touch that moves less than this (px) is a tap, not a scroll. */
  private static readonly TAP_SLOP = 6;

  private readonly player_slot_y: number;
  private readonly rows_clip: HTMLDivElement;
  private readonly player_card: HTMLCanvasElement | null;
  private player_row: TournamentStandingsRow | null = null;
  private tap_start: Vector2 | null = null;
  private player_card_y = Number.NaN;
  private rows_clip_path = "";
  private readonly rows: ListRow[] = [];

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    viewport_h: number,
    standings: TournamentStanding[]
  )
  {
    const w = LayoutScaler.widthFromDesign( TournamentStandingsList.ITEM_W );

    super( ctx, x, y, w, viewport_h, {
      scroll_speed: 1,
      enable_momentum: true,
      enable_touch_scroll: true,
      scroll_sensitivity: 0.8,
    } );

    const item_h = LayoutScaler.heightFromDesign( TournamentStandingsList.ITEM_H );
    const step = LayoutScaler.heightFromDesign( TournamentStandingsList.ROW_STEP );
    const slotY = ( rank: number ): number => ( rank - 1 ) * step;

    this.setContentHeight( slotY( standings.length ) + item_h, step, 0 );

    for ( const standing of standings )
    {
      if ( standing.is_player ) continue;

      const slot_y = slotY( standing.rank );
      this.rows.push( {
        row: new TournamentStandingsRow( this.getContentCtxAt( slot_y ), 0, slot_y, w, item_h, standing ),
        slot_y,
      } );
    }

    // Every row is drawn up front, as the leaderboard's are: the list appears with the dialog
    // instead of rows sliding in one by one, which repainted the canvas every frame and lagged.
    for ( const entry of this.rows )
      entry.row.render();

    const player = standings.find( standing => standing.is_player );
    this.player_slot_y = player ? slotY( player.rank ) : 0;
    this.rows_clip = this.wrapRowsCanvas();
    this.player_card = player ? this.createPlayerCard( w, item_h, player ) : null;
    this.updatePlayerCard();
    // Touch moves shift the row canvases right in the event handler; following only from update()
    // left the card a frame behind whenever the event landed after the frame's rAF (a rare hop).
    this.setOnScrollChange( () => this.updatePlayerCard() );
  }

  public override name(): string { return "TournamentStandingsList"; }

  public override onTouchStart( p: Vector2, now: number ): boolean
  {
    const handled = super.onTouchStart( p, now );
    this.tap_start = handled ? p.clone() : null;
    return handled;
  }

  public override onTouchMove( p: Vector2, now: number ): void
  {
    if ( this.tap_start && !this.isTap( p ) )
      this.tap_start = null;
    super.onTouchMove( p, now );
  }

  public override onTouchEnd( p: Vector2, now: number, p_global: Vector2 | null = null ): void
  {
    const tapped = this.tap_start !== null && this.isTap( p );
    this.tap_start = null;
    super.onTouchEnd( p, now, p_global );
    if ( tapped )
      this.showTooltipAt( this.toLocal( p ) );
  }

  public override destroy(): void
  {
    DomTooltip.hide();
    super.destroy();
  }

  private isTap( p: Vector2 ): boolean
  {
    const start = this.tap_start;
    const slop = TournamentStandingsList.TAP_SLOP;
    return start !== null && Math.abs( p.x - start.x ) <= slop && Math.abs( p.y - start.y ) <= slop;
  }

  private showTooltipAt( point: Vector2 ): void
  {
    const hit = this.rowAt( point );
    if ( !hit )
      return;

    const slot = hit.row.prizeSlots().find( entry => TournamentStandingsList.contains( entry.bounds, hit.row_y, point ) );
    if ( slot )
    {
      const anchor = this.toGameRect( slot.bounds, hit.row_y );
      if ( isPlaceBoxItem( slot.item ) )
        DomTooltip.showRewards( anchor, slot.item.rewards, { align_left: true } );
      else
        DomTooltip.showText( anchor, RewardTooltip.nameOfLegacy( slot.item.type ) );
      return;
    }

    const crowns = hit.row.crownsBounds();
    if ( TournamentStandingsList.contains( crowns, hit.row_y, point ) )
      DomTooltip.showText( this.toGameRect( crowns, hit.row_y ), LocalizationManager.t( "TURNAMENT_SCORE" ) );
  }

  /** The row under a list-local point: the pinned player card first, then the scrolled rows. */
  private rowAt( point: Vector2 ): { row: TournamentStandingsRow; row_y: number } | null
  {
    const rect = this.getRect();
    if ( point.x < 0 || point.x > rect.getW() || point.y < 0 || point.y > rect.getH() )
      return null;

    const card_h = LayoutScaler.heightFromDesign( TournamentStandingsList.ITEM_H );
    if ( this.player_row && point.y >= this.player_card_y && point.y <= this.player_card_y + card_h )
      return { row: this.player_row, row_y: this.player_card_y };

    for ( const entry of this.rows )
    {
      const row_y = entry.row.pixelPosImportant().y - this.getScrollOffset();
      if ( point.y >= row_y && point.y <= row_y + card_h )
        return { row: entry.row, row_y };
    }
    return null;
  }

  private static contains( bounds: RowBounds, row_y: number, point: Vector2 ): boolean
  {
    return point.x >= bounds.x && point.x <= bounds.x + bounds.w
      && point.y >= row_y + bounds.y && point.y <= row_y + bounds.y + bounds.h;
  }

  /** Row-local bounds to game-canvas pixels, for the page-level tooltip. */
  private toGameRect( bounds: RowBounds, row_y: number ): TooltipAnchorRect
  {
    const pos = this.getAbsolutePosition();
    return { x: pos.x + bounds.x, y: pos.y + row_y + bounds.y, w: bounds.w, h: bounds.h };
  }

  public override update( now: number ): void
  {
    super.update( now );
    this.updatePlayerCard();
  }

  private wrapRowsCanvas(): HTMLDivElement
  {
    const rows_canvases = this.getContentCanvases();
    const wrapper = document.createElement( "div" );
    wrapper.style.cssText = "position:absolute;left:0;top:0;right:0;bottom:0;pointer-events:none";
    this.getOverlayHost().insertBefore( wrapper, rows_canvases[0] );
    for ( const canvas of rows_canvases )
      wrapper.appendChild( canvas );
    return wrapper;
  }

  private createPlayerCard( w: number, h: number, player: TournamentStanding ): HTMLCanvasElement
  {
    const dpr = Game.getDpr();
    const canvas = document.createElement( "canvas" );
    canvas.width = Math.ceil( w * dpr );
    canvas.height = Math.ceil( h * dpr );
    canvas.style.cssText = [
      "position:absolute", "left:0", "top:0", "pointer-events:none",
      `width:${w}px`, `height:${h}px`,
    ].join( ";" );
    this.getOverlayHost().appendChild( canvas );
    const card_ctx = canvas.getContext( "2d" )!;
    card_ctx.scale( dpr, dpr );
    this.player_row = new TournamentStandingsRow( card_ctx, 0, 0, w, h, player );
    this.player_row.render();

    return canvas;
  }

  private updatePlayerCard(): void
  {
    const card_h = LayoutScaler.heightFromDesign( TournamentStandingsList.ITEM_H );
    const edge = LayoutScaler.heightFromDesign( TournamentStandingsList.ROWS_EDGE_CLIP );
    const under_card = card_h - LayoutScaler.heightFromDesign( TournamentStandingsList.ROWS_UNDER_CARD );
    const pinned_y = this.getRect().getH() - card_h;
    const slot_y = this.player_slot_y - this.getScrollOffset();
    const pinned_top = this.player_card !== null && slot_y < 0;
    const pinned_bottom = this.player_card !== null && slot_y > pinned_y;
    const clip_path = `inset(${pinned_top ? under_card : edge}px 0px ${pinned_bottom ? under_card : edge}px 0px)`;
    if ( clip_path !== this.rows_clip_path )
    {
      this.rows_clip_path = clip_path;
      this.rows_clip.style.clipPath = clip_path;
    }
    if ( !this.player_card ) return;
    const y = Math.max( 0, Math.min( slot_y, pinned_y ) );
    if ( y === this.player_card_y )
      return;

    this.player_card_y = y;
    this.player_card.style.transform = `translate3d(0,${y}px,0)`;
  }
}
